import * as React from 'react';
import { useMediaQuery } from 'react-responsive';

import { InformationBoxTheme, Color, IconName, ButtonTheme, Breakpoint, DirectionEnum } from '../../enums';
import { Button } from '../button/button.component';
import { Icon } from '../icon/icon.component';
import { MenuPosition } from '../menuPosition/menuPosition.component';

import { 
  InformationDetails, 
  AvatarContainer, 
  AvatarImg, 
  Container, 
  Name, 
  NamesContainer, 
  SubName, 
  StyledButton,
  InformationBoxContainer,
  RemoveButton,
  ArrowsContainer,
  ArrowButton,
  Wrapper
} from './informationBox.styled';
import { InformationBoxProps } from './informationBox.types';

export const InformationBox: React.FC<InformationBoxProps> = (props: InformationBoxProps): JSX.Element => {
  const {
    className,
    informationDetails,
    theme = InformationBoxTheme.WithOutline,
    onRemove,
    onPositionChangeAsync,
    setIsLoading,
    isFirst,
    isLast
  }: InformationBoxProps = props;
  const isSmallMobile: boolean = useMediaQuery({ query: Breakpoint.SmallMobile });

  const onPositionChangeAction: (dir: DirectionEnum) => void = (dir: DirectionEnum): void => {
    if (!onPositionChangeAsync || !setIsLoading) {
      return;
    }
    setIsLoading(true);
    onPositionChangeAsync(dir)
      .then((): void => setIsLoading(false))
      .catch((): void => setIsLoading(false));
  };

  return (
    <Wrapper className={className}>
      <Container>
        <InformationBoxContainer $theme={theme}>
          <InformationDetails>
            <AvatarContainer $withCustomIcon={!!informationDetails.iconName}>
              {informationDetails.avatar ? (
                <AvatarImg src={informationDetails.avatar} alt={informationDetails.name} />
              ) : (
                <Icon
                  name={informationDetails.iconName ?? IconName.Alien}
                  size={20}
                  color={informationDetails.iconName ? Color.White : Color.Gray400}
                />
              )}
            </AvatarContainer>
            <NamesContainer>
              <MenuPosition {...informationDetails.nameUrlSettings}>
                <Name>{informationDetails.name}</Name>
              </MenuPosition>
              {informationDetails.subName && <SubName text={informationDetails.subName} />}
            </NamesContainer>
          </InformationDetails>
          {!isSmallMobile && !!informationDetails.buttonLabel && !!informationDetails.onClick && (
            <Button
              label={informationDetails.buttonLabel}
              onClick={informationDetails.onClick}
              buttonTheme={ButtonTheme.SecondarySolidSmall}
              id='information-box-button'
            />
          )}
          {!!onRemove && (
            <RemoveButton onClick={onRemove} data-testid='remove-button'>
              <Icon name={IconName.Delete} size={20} />
            </RemoveButton>
          )}
        </InformationBoxContainer>
        {isSmallMobile && !!informationDetails.buttonLabel && !!informationDetails.onClick && (
          <StyledButton
            label={informationDetails.buttonLabel}
            onClick={informationDetails.onClick}
            buttonTheme={ButtonTheme.SecondarySolidSmall}
            fullWidth
          />
        )}
      </Container>
      {!!onPositionChangeAsync && (
        <ArrowsContainer data-testid='arrows-container'>
          <ArrowButton 
            onClick={(): void => onPositionChangeAction(DirectionEnum.Up)} 
            disabled={isFirst}
            data-testid='arrow-up-button'
          >
            <Icon 
              name={IconName.ArrowUp15} 
              size={16} 
              color={isFirst ? Color.Gray400 : Color.Error500} 
            />
          </ArrowButton>
          <ArrowButton 
            onClick={(): void => onPositionChangeAction(DirectionEnum.Down)} 
            disabled={isLast}
            data-testid='arrow-down-button'
          >
            <Icon 
              name={IconName.ArrowDown15} 
              size={16} 
              color={isLast ? Color.Gray400 : Color.Error500} 
            />
          </ArrowButton>
        </ArrowsContainer>
      )}
    </Wrapper>
  );
};
